/**
 * Connexion par QR code depuis l'app mobile (comme WhatsApp Web).
 * L'ordinateur affiche un QR code et un code de secours, renouvelés toutes les 60 s ;
 * le téléphone les scanne, l'utilisateur confirme, et la session arrive ici par le suivi.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import axiosInstance from '../../../config/axios';
import { ArrivalOverlay, LinkTransfer } from './LinkTransfer';

const POLL_MS = 2000;
const MAX_ROUNDS = 5; // après 5 QR non scannés (≈ 5 min), on attend un clic pour relancer
const ARRIVAL_MS = 2600; // durée de l'animation de transfert avant d'ouvrir la session

export default function QrLoginPanel({ onApproved }) {
  const [link, setLink] = useState(null); // { requestId, pollSecret, qr, code, expiresIn }
  const [status, setStatus] = useState('loading'); // loading | pending | scanned | approved | rejected | paused | error
  const [arrival, setArrival] = useState(null); // { user, origin } : voile plein écran après confirmation
  const reduce = useReducedMotion();
  const boxRef = useRef(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const rounds = useRef(0);
  const doneRef = useRef(false);

  const start = useCallback(async () => {
    setStatus('loading');
    try {
      const { data } = await axiosInstance.post('/auth/qr/start');
      if (!data?.success) throw new Error('start');
      rounds.current += 1;
      setLink(data);
      setSecondsLeft(data.expiresIn);
      setStatus('pending');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    start();
  }, [start]);

  // Compte à rebours : QR renouvelé à l'expiration (sauf s'il a été scanné).
  useEffect(() => {
    if (status !== 'pending') return undefined;
    if (secondsLeft <= 0) {
      if (rounds.current >= MAX_ROUNDS) setStatus('paused');
      else start();
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [status, secondsLeft, start]);

  // Suivi de la demande : scannée, approuvée (session reçue), refusée, expirée.
  useEffect(() => {
    if (!link || !['pending', 'scanned'].includes(status)) return undefined;
    let cancelled = false;
    const t = setInterval(async () => {
      try {
        const { data } = await axiosInstance.get('/auth/qr/status', {
          params: { requestId: link.requestId, secret: link.pollSecret },
        });
        if (cancelled || doneRef.current) return;
        if (data.status === 'approved' && data.user && data.token) {
          doneRef.current = true;
          clearInterval(t);
          // Animation de transfert (coche sur l'ordinateur, puis voile « Bienvenue ») avant d'ouvrir la session
          setStatus('approved');
          if (reduce) {
            onApproved?.(data);
            return;
          }
          const r = boxRef.current?.getBoundingClientRect();
          setTimeout(() => {
            setArrival({ user: data.user, origin: r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null });
          }, 1100);
          setTimeout(() => onApproved?.(data), 1100 + ARRIVAL_MS);
        } else if (data.status === 'scanned') {
          setStatus('scanned');
        } else if (data.status === 'rejected') {
          setStatus('rejected');
        } else if (data.status === 'expired') {
          rounds.current = 0;
          start();
        }
      } catch {
        // coupure réseau passagère : on réessaie au prochain tour
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [link, status, start, onApproved, reduce]);

  const restart = () => {
    rounds.current = 0;
    start();
  };

  return (
    <div className="w-full max-w-[640px] bg-white rounded-[28px] shadow-[0_24px_60px_rgba(15,23,42,0.12)] px-8 py-9 sm:px-12 text-left">
      <h2 className="text-xl font-bold text-gray-900">Se connecter avec le téléphone</h2>
      <ol className="mt-4 space-y-2 text-sm text-gray-600 list-decimal list-inside">
        <li>Ouvrez l’app Secura sur votre téléphone.</li>
        <li>
          Dans un projet, touchez <span className="font-semibold text-gray-900">Ouvrir sur l’ordinateur</span>
          {' '}(ou Profil › <span className="font-semibold text-gray-900">Appareils connectés</span>).
        </li>
        <li>Scannez ce code, puis confirmez sur le téléphone.</li>
      </ol>

      <div className="mt-6 flex flex-col items-center">
        <div
          ref={boxRef}
          className={`relative flex items-center justify-center rounded-2xl bg-white ${
            status === 'scanned' || status === 'approved' ? 'w-full max-w-[560px] h-[300px]' : 'w-[232px] h-[232px]'
          }`}
        >
          <AnimatePresence mode="wait" initial={false}>
            {link && ['pending', 'scanned', 'approved', 'paused', 'rejected'].includes(status) ? (
              <motion.div
                key={`qr-${link.requestId}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              >
                <QRCodeSVG
                  value={link.qr}
                  size={216}
                  level="M"
                  marginSize={2}
                  className={`transition-[opacity,filter] duration-300 ${status === 'pending' ? '' : status === 'scanned' || status === 'approved' ? 'opacity-0' : 'opacity-10 blur-[2px]'}`}
                  aria-label="QR code de connexion"
                />
              </motion.div>
            ) : (
              <div key="skeleton" className="w-[216px] h-[216px] rounded-xl bg-gray-100 animate-pulse" />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {status === 'scanned' || status === 'approved' ? <LinkTransfer key="transfer" stage={status} /> : null}
          </AnimatePresence>

          {(status === 'paused' || status === 'rejected' || status === 'error') && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <p className="text-sm text-gray-700 mb-3">
                {status === 'rejected'
                  ? 'Connexion refusée sur le téléphone.'
                  : status === 'error'
                    ? 'Impossible de générer le code.'
                    : 'Le code a expiré.'}
              </p>
              <button
                type="button"
                onClick={restart}
                className="px-4 py-2 rounded-full bg-[#0026E2] text-white text-sm font-semibold"
              >
                Nouveau code
              </button>
            </div>
          )}
        </div>

        <AnimatePresence mode="wait">
          {status === 'scanned' || status === 'approved' ? (
            <motion.div
              key={status}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="mt-4 text-center"
              aria-live="polite"
            >
              <p className="text-sm font-semibold text-gray-900">
                {status === 'approved' ? 'Connexion confirmée' : 'Code scanné'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {status === 'approved' ? 'Transfert de votre session vers cet ordinateur…' : 'Confirmez la connexion sur votre téléphone.'}
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {link && status === 'pending' ? (
          <>
            <p className="mt-4 text-xs text-gray-500">Le scan ne marche pas ? Tapez ce code dans l’app :</p>
            <p className="mt-1 font-mono text-2xl font-bold tracking-[0.2em] text-gray-900 select-all">{link.code}</p>
            <p className="mt-2 text-xs text-gray-400 tabular-nums">Nouveau code dans {secondsLeft} s</p>
          </>
        ) : null}
      </div>
      {arrival ? <ArrivalOverlay user={arrival.user} origin={arrival.origin} duration={ARRIVAL_MS} /> : null}
    </div>
  );
}
